import { AppContext, Features } from "@lightspeed-invoicing/core-client/frontend"
import React, { useEffect, useState } from "react"
import { ListGroup, Modal } from "react-bootstrap"
import { FormattedMessage, FormattedNumber, injectIntl } from "react-intl"
import ActionButton from "../common/button/actionbutton.component.jsx"
import Checkbox from "../common/checkbox/checkbox.component.jsx"
import Icon from "../common/icon/icon.component.jsx"
import StandardList from "../common/listing/list.component.jsx"
import LoadingSpinner from "../common/spinner/loading.component.jsx"
import InputUnitOfMeasure from "../common/unit-of-measure/input.component.jsx"
import CustomerSelect from "../customer/selection.component.jsx"
import { GlobalHelper } from "../indexImports.js"
import LocationSelect from "../location/locationselect.component.jsx"
import CreditMemo from "../state/credit-memo/CreditMemo"
import CreditMemoService from "../state/credit-memo/CreditMemoService"
import { isError } from "../state/helpers/Error"
import { Intl } from "../state/helpers/IntlProps"
import { CreateOrderDto, DuplicateOrderDto } from "../state/order/Order.js"
import OrderService from "../state/order/OrderService"
import QuoteService from "../state/quote/QuoteService"
import { Row } from "../state/rows/Row"
import RowQuantityService from "../state/rows/RowQuantityService"
import { isBundle } from "../utils"

type Props = {
    intl: Intl
    modalId: string
    quoteId: string,
    contactId: string,
    projectId: string,
    focused: boolean,
    sellerId: string,
    loading: boolean,
    createOrderDisabled: boolean,
    checkAll: boolean,
    creditMemos: CreditMemo[],
    selectedCreditMemos: string[],
    groups: object,
    itemsListToOrder: Row[],
    locationId: string,
    onClose: (orderId: string) => object
    fromProjectId: string,
    fromOrderId: string,
}

const NewOrderModal = (props: Props) => {
    const [contactId, setCustomer] = useState<string>(props.contactId || "")
    const [projectId] = useState<string>(props.projectId || "")
    const [locationId, setLocation] = useState<string>(
        props.locationId ? props.locationId : (AppContext.getUserLoggedIn().attributes.location?.id ?? ""),
    )
    const [sellerId] = useState<string>(props.sellerId || AppContext.getUserLoggedIn().id)
    const [nbOfItemsChecked, setNbOfItemsChecked] = useState<number>(0)
    const [selectedCreditMemos, setSelectedCreditMemos] = useState<string[]>([])
    const [groups] = useState<object>(props.groups)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [itemsListToOrder, setItemsListToOrder] = useState<Row[]>([])
    const [creditMemos, setCreditMemos] = useState<CreditMemo[]>([])

    useEffect(() => {
        if (props.quoteId) {
            QuoteService.get(props.quoteId).then(res => {
                if (!isError(res)) {
                    const {itemsListToOrder} = RowQuantityService.getItemListOrderedFromQuote(res)
                    CreditMemoService.getCreditMemosFromQuoteId(props.quoteId).then(creditMemosData => {
                        if (!isError(creditMemosData) && creditMemosData) {
                            let notTranferredCreditMemos: CreditMemo[] = []
                            if (!isError(creditMemosData)) {
                                notTranferredCreditMemos = creditMemosData.filter(cm => !cm.getAttributes().orderReferenceId)
                            }

                            setCreditMemos(notTranferredCreditMemos)
                            setSelectedCreditMemos(notTranferredCreditMemos.map(cm => cm.getId()))
                            setItemsListToOrder(itemsListToOrder)
                        }
                    })
                }
            })
        }
        setIsLoading(false)
    }, [])

    useEffect(() => {
        checkStateHandler()
        validateCompletedList(itemsListToOrder)
    }, [itemsListToOrder])

    const closeModal = (orderId) => {
        if (props.onClose) {
            props.onClose(orderId)
        }
    }

    const checkStateHandler = () => {
        setNbOfItemsChecked(itemsListToOrder.filter(item => item.checked).length)
    }

    const onCheckAll = (isChecked) => {
        setItemsListToOrder(itemsListToOrder.map(item => {
            item.checked = !item.disabled ? isChecked : false
            item.attributes.measureToOrder = isChecked ? item.attributes.maxMeasureOrdered : 0
            return item
        }))
        setSelectedCreditMemos(creditMemos.map(cm => cm.getId()))
        checkStateHandler()
    }

    const onItemChecked = (item, isChecked) => {
        item.checked = isChecked
        item.attributes.measureToOrder =
            isChecked ? item.attributes.maxMeasureOrdered : 0
        setItemsListToOrder([...itemsListToOrder])
        checkStateHandler()
    }

    const isAllCheck = () => {
        return nbOfItemsChecked == itemsListToOrder.length
    }

    const isChecked = () => {
        return nbOfItemsChecked > 0
    }

    const validateCompletedList = (itemsListToOrder) => {
        let selectedCreditMemosChange = selectedCreditMemos.slice()
        if (isItemListToOrderCompleted(itemsListToOrder)) {
            selectedCreditMemosChange = creditMemos.map(cm => cm.getId())
        }
        setSelectedCreditMemos(selectedCreditMemosChange)
    }

    const isItemListToOrderCompleted = (itemsListToOrder) => {
        let ordered = true
        itemsListToOrder.map(item => {
            const totalMeasureApplied = parseFloat(item.attributes.measureToOrder) + parseFloat(item.attributes.orderedMeasure)
            if (totalMeasureApplied < parseFloat(item.attributes.totalMeasure)) {
                ordered = false
            }
        })
        return ordered
    }


    const onItemInputChange = (item, val) => {

        if (parseFloat(val) > item.attributes.maxMeasureOrdered)
            val = item.attributes.maxMeasureOrdered
        else if (parseFloat(val) < 1)
            val = 1

        if (parseFloat(val) > 0)
            item.checked = true

        item.attributes.measureToOrder = val
        setItemsListToOrder([...itemsListToOrder])
    }

    const saveOrder = async () => {
        const data: CreateOrderDto = {
            sellerId: sellerId,
            projectId: projectId,
            contactId: contactId,
            quoteReferenceId: props.quoteId,
            locationId: locationId,
        }

        if (props.quoteId) {
            data.creditMemoIdsToApply = selectedCreditMemos
            data.reference = {
                objectId: props.quoteId,
                type: "quote"
            }
            data.rows = itemsListToOrder.filter(item => item.checked && item.attributes.measureToOrder > 0).map(item => {
                return {
                    referenceRowId: item.id,
                    measure: String(item.attributes.measureToOrder),
                    productId: item.attributes.itemObject.id,
                    groupId: item.attributes.groupId,
                    status: item.attributes.type,
                }
            })
        }
        if (props.fromProjectId && props.fromOrderId) {
            const data: DuplicateOrderDto = {
                contactId: contactId,
                locationId: locationId,
            }
            const ord = await OrderService.duplicateOrder(props.fromOrderId, data)
            if (!isError(ord)) {
                return closeModal(ord.getId())
            }
        } else {
            const ord = await OrderService.createOrder(data)
            if (!isError(ord)) {
                return closeModal(ord.getId())
            }
        }
    }

    const handleCreditMemosChange = (id, isChecked) => {
        let newCreditMemos = selectedCreditMemos.slice()
        if (isItemListToOrderCompleted(itemsListToOrder)) {
            newCreditMemos = creditMemos.map(cm => cm.getId())
        } else {
            if (id && isChecked) {
                newCreditMemos.push(id)
            }
            if (id && !isChecked) {
                newCreditMemos = newCreditMemos.filter(cmId => cmId != id)
            }
        }
        setSelectedCreditMemos(newCreditMemos)
    }

    const modalTitle = () => {
        return <FormattedMessage
            id="order.modal.creation.title"
            defaultMessage="Create an order"
        />
    }

    const headers = () => {
        let headers = [
            <FormattedMessage
                key="invoice.modal.creation.product_name_label"
                id="invoice.modal.creation.product_name_label"
                defaultMessage="Product name"
            />,
            <FormattedMessage
                key="invoice.modal.creation.quantity_to_order_label"
                id="invoice.modal.creation.quantity_to_order_label"
                defaultMessage="Max Quantity to Order"
            />,
            <FormattedMessage
                key="invoice.modal.creation.quantity_already_ordered_label"
                id="invoice.modal.creation.quantity_already_ordered_label"
                defaultMessage="Already ordered"
            />]

        if (AppContext.hasFeatureIntegration(Features.InventorySync)) {
            headers = [
                ...headers,
                <FormattedMessage
                    key="invoice.modal.creation.qty_in_stock_label"
                    id="invoice.modal.creation.qty_in_stock_label"
                    defaultMessage="Quantity in stock"
                />]
        }

        return [...headers,
            <FormattedMessage
                key="invoice.modal.creation.quantity_to_be_ordered_label"
                id="invoice.modal.creation.quantity_to_be_ordered_label"
                defaultMessage="To be ordered"
            />,
            <Checkbox id={"check-all"} key={"check-all"}
                checked={isChecked()}
                isPartiallyChecked={!isAllCheck()}
                onChange={isChecked => onCheckAll(isChecked)}/>]
    }

    const formatCreditMemoRow = (cm) => {
        return <ListGroup.Item>
            <label className="row justify-content-between">
                <div>
                    <Checkbox className={"mr-2"} checked={selectedCreditMemos.includes(cm.getId())} id={cm.getId()}
                        onChange={(e) => handleCreditMemosChange(cm.getId(), e)}/>
                    {cm.getAttributes().identifier}
                </div>
                <div>
                    <FormattedNumber
                        style="currency"
                        currency={cm.getAttributes().currency}
                        value={cm.getAttributes().amount}
                    />
                </div>
            </label>
        </ListGroup.Item>
    }

    const row = (item) => {
        let row = [
            <div key={0} className="text-break">
                {item.attributes.activeTitle}
            </div>,

            <div key={1} className="text-center">
                {item.attributes.totalMeasure}
            </div>,

            <div key={2} className="text-center">
                {item.attributes.orderedMeasure}
            </div>
        ]

        if (AppContext.hasFeatureIntegration(Features.InventorySync)) {
            row = [...row, <div key={3} className="text-center">
                {item.attributes.itemObject.attributes.quantityOnHand}
            </div>]
        }

        const data = [...row,
            <div key={4} className="input-group m-1">
                <InputUnitOfMeasure
                    disabled={item.disabled || isBundle(item)}
                    debounced={true}
                    value={parseFloat(item.attributes.measureToOrder) > 0 ? item.attributes.measureToOrder : "0"}
                    unit={item.attributes.unitOfMeasure}
                    onChange={val => onItemInputChange(item, val)}
                    format={"short"}
                />
            </div>,
            !item.disabled && <Checkbox id={"returned-item-" + item.id}
                checked={item.checked}
                onChange={isChecked => onItemChecked(item, isChecked)}
                style={{top: 0}}/>
        ]

        const rowClassNames: string[] = []
        for (let i = 0; i < data.length; i++) {
            rowClassNames.push("px-4 py-2")
        }
        // Change style for measure column
        rowClassNames[rowClassNames.length - 2] = "col-xs-4 col-sm-3 col-lg-2 px-0 py-2"
        return {
            data: data,
            classNames: rowClassNames,
        }
    }


    return (
        <form className="needs-validation" noValidate id="work-order-form-creation-modal">
            <Modal show={true} size="lg" onHide={() => closeModal("")} centered={true}>
                <Modal.Header>
                    <Modal.Title>
                        <div className="modal-title h5"
                            id={props.modalId ? props.modalId + "Label" : "order-creation-modalLabel"}>
                            {modalTitle()}
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!isLoading ? <div className="container-fluid pb-2 pl-4 pr-4 pt-2">
                        <div className="form-group row mt-3">
                            <label htmlFor="contactId" className="col-lg-3 col-12 col-form-label">
                                <FormattedMessage
                                    id="common.order.modal.customer_label"
                                    defaultMessage="Order to"
                                />
                            </label>
                            <div className="col-lg-9 col-12">
                                <CustomerSelect
                                    id={"contactId"}
                                    contactType={"persons"}
                                    value={contactId}
                                    onChange={setCustomer}
                                />
                            </div>
                        </div>
                        {AppContext.isMultiLocation() &&
                                <div className="form-group row mt-3 mb-4">
                                    <label htmlFor="locationId" className="col-lg-3 col-12 col-form-label">
                                        <FormattedMessage
                                            id="common.order.modal.location_label"
                                            defaultMessage="Location"
                                        />
                                    </label>
                                    <div className="col-lg-9 col-12">
                                        <LocationSelect
                                            value={locationId}
                                            onChange={setLocation}
                                            className="form-control"
                                        />
                                    </div>
                                </div>
                        }
                        {!isLoading && props.quoteId && <div className="w-100">
                            <StandardList
                                className="row"
                                headers={headers()}
                                rows={itemsListToOrder.sort((item1, item2) =>
                                    GlobalHelper.compareItemsByGroupAndRank(item1, item2, groups)).map(item =>
                                    row(item))
                                }
                                rowClassName={" "}
                                smallLine={true}
                            />
                            {creditMemos.length > 0 && <div className="mb-3 col">
                                <div className="mb-2 mt-3">
                                    <FormattedMessage
                                        id="common.order.modal.apply_to_order_btn"
                                        defaultMessage="Apply to Order"
                                    />
                                </div>

                                <div className="col-9 d-flex align-items-top-center"
                                    style={{scrollBehavior: "smooth", overflowY: "scroll", height: "120px"}}>
                                    <ListGroup className="w-100">
                                        {creditMemos.map(x => formatCreditMemoRow(x))}
                                    </ListGroup>
                                </div>

                            </div>}
                        </div>}
                    </div> :
                        <div className="container-fluid">
                            <LoadingSpinner className="py-2"/>
                        </div>}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={() => closeModal("")}>
                        <FormattedMessage
                            id="common.invoice.modal.close_btn"
                            defaultMessage="Close"
                        />
                    </button>
                    <ActionButton disabled={contactId == ""} className="btn btn-primary" onClick={saveOrder}>
                        <Icon value={"invoice"} className={"mr-1"}/>
                        <FormattedMessage
                            id="common.order.modal.confirm_btn"
                            defaultMessage="Create order"
                        />
                    </ActionButton>
                </Modal.Footer>
            </Modal>
        </form>
    )

}

export default injectIntl(NewOrderModal)





// WEBPACK FOOTER //
// ./src/order/NewOrderModal.tsx